import { FormEvent, useEffect, useRef, useState } from 'react';

import { OtpInput, ResendCode, useResendCountdown } from '@/auth/components/shared';
import { codeSentMessage } from '@/auth/utils/codeDestination';
import { confirmEmail, enableEmailMfa, sendEmailVerification } from '@/auth/utils/cognitoMfa';
import type { CognitoMfaType } from '@/auth/utils/cognitoSignIn';
import Button from '@/componentsv2/Button';
import { OTP_METHODS } from '@/core/api';

import { type RunExclusive, mfaErrorMessage } from './mfaErrorMessage';

const CODE_LENGTH = 6;
const ERROR_ID = 'cognito-email-enrolment-error';

interface CognitoEmailEnrolmentProps {
  /** The methods on now, turned off when email goes on. */
  turnOff?: CognitoMfaType[];
  onEnrolled: () => void;
  onCancel: () => void;
  /** Holds the settings page's change lock for each request. */
  runExclusive: RunExclusive;
}

/**
 * Email factor (ENG-6323): email codes go on only once a code Cognito emails
 * to the account's address is entered. The code is sent as the step opens.
 */
export default function CognitoEmailEnrolment({
  turnOff,
  onEnrolled,
  onCancel,
  runExclusive,
}: CognitoEmailEnrolmentProps): JSX.Element {
  const [destination, setDestination] = useState<string>();
  const [sendFailed, setSendFailed] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  // The code is spent once confirmed: a failure after that retries the turn-on alone.
  const [confirmed, setConfirmed] = useState(false);
  const countdown = useResendCountdown(false);
  // Once per dialog, StrictMode's second effect run included.
  const sent = useRef(false);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(undefined);
    try {
      await runExclusive(action);
    } catch (err) {
      setError(mfaErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const send = () =>
    run(async () => {
      setSendFailed(false);
      try {
        setDestination((await sendEmailVerification()) ?? '');
      } catch (err) {
        setSendFailed(true);
        throw err;
      }
      countdown.restart();
      setCode('');
    });

  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    void send();
    // Sent once as the step opens; Resend sends again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    void run(async () => {
      if (!confirmed) {
        await confirmEmail(code);
        setConfirmed(true);
      }
      await enableEmailMfa(turnOff);
      onEnrolled();
    });
  }

  function sentLine(): string {
    if (destination !== undefined) {
      return `${codeSentMessage(OTP_METHODS.EMAIL, destination) ?? 'We emailed you a code'}. Enter it below.`;
    }
    return sendFailed
      ? 'We could not email you a code. Use Resend code to try again.'
      : 'Sending a code to your email…';
  }

  return (
    <form className="flex flex-col gap-16" onSubmit={handleSubmit}>
      <p className="text-on-surface">{sentLine()}</p>
      <OtpInput
        value={code}
        onChange={setCode}
        length={CODE_LENGTH}
        disabled={busy || confirmed}
        autoFocusFirstCell
        hasError={!!error}
        aria-describedby={error ? ERROR_ID : undefined}
        // Six 44px cells fit a phone-width dialog only by using part of its padding.
        className="-mx-16 flex justify-center gap-6 sm:mx-[0] sm:gap-10"
      />
      {error && (
        <p id={ERROR_ID} className="text-14 text-[var(--errorText)]" role="alert">
          {error}
        </p>
      )}
      {!confirmed && (
        <ResendCode
          className="self-start"
          secondsLeft={countdown.secondsLeft}
          onResend={() => void send()}
          disabled={busy}
        />
      )}
      <div className="flex justify-end gap-8">
        <Button
          type="button"
          variant="tertiary"
          // Tertiary teal is 3.2:1 on the dark dialog: brand-primary there, as ResendCode.
          className="dark:!text-brand-primary"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={code.length < CODE_LENGTH || busy}>
          Turn on
        </Button>
      </div>
    </form>
  );
}
